/** Presentation only. Opposed totals use caster >= defender; natural 1/20 are not
 * automatic contest outcomes (unlike attack hits). Never used to resolve a roll. */
export interface EstimateRoll { tierDie: number; modifier: number; stacks: number; forcedNatural?: number }
export interface TargetSuccessEstimate {
  entityId: string;
  kind: 'contest' | 'hit' | 'cast';
  state: 'estimated' | 'unknown' | 'hidden';
  percent?: number;
}
export function rollDistribution(roll: EstimateRoll): Map<number, number> {
  const result = new Map<number, number>();
  const tier = Math.max(0, Math.min(100, Math.trunc(roll.tierDie)));
  const stacks = Math.max(-100, Math.min(100, Math.trunc(roll.stacks)));
  const count = 1 + Math.abs(stacks);
  for (let d20 = 1; d20 <= 20; d20++) {
    const p = roll.forcedNatural === 1 || roll.forcedNatural === 20 ? Number(d20 === roll.forcedNatural)
      : stacks > 0 ? (d20 / 20) ** count - ((d20 - 1) / 20) ** count
      : stacks < 0 ? ((21 - d20) / 20) ** count - ((20 - d20) / 20) ** count : 1 / 20;
    for (let bonus = tier ? 1 : 0; bonus <= tier; bonus++) {
      const total = d20 + Math.trunc(roll.modifier) + bonus;
      result.set(total, (result.get(total) ?? 0) + p / (tier || 1));
    }
  }
  return result;
}
export function estimateContest(caster: EstimateRoll | number, defender: EstimateRoll): number {
  const attack = typeof caster === 'number' ? new Map([[caster, 1]]) : rollDistribution(caster);
  const defense = rollDistribution(defender);
  let win = 0;
  for (const [a, ap] of attack) for (const [b, bp] of defense) if (a >= b) win += ap * bp;
  return Math.max(0, Math.min(100, Math.round(win * 100 + 1e-9)));
}
/** Armor ties continue the spell (with wild magic); this is not an overall spell-success chance. */
export function estimateCastCheck(roll: EstimateRoll, dc: number): number {
  let pass = 0;
  for (const [total, probability] of rollDistribution(roll)) if (total >= dc) pass += probability;
  return Math.max(0, Math.min(100, Math.round(pass * 100 + 1e-9)));
}
export function parseSuccessEstimates(value: unknown): TargetSuccessEstimate[] {
  if (!Array.isArray(value)) return [];
  return value.slice(0, 256).flatMap<TargetSuccessEstimate>(raw => {
    if (!raw || typeof raw !== 'object') return [];
    const r = raw as Record<string, unknown>;
    const entityId = String(r.entityId ?? '').toUpperCase();
    if (!/^[0-9A-F]{8}(?:-[0-9A-F]{4}){3}-[0-9A-F]{12}$/.test(entityId)
      || !['contest', 'hit', 'cast'].includes(String(r.kind))) return [];
    const kind = r.kind as TargetSuccessEstimate['kind'];
    // Redacted state never retains a number, even if a malformed sender includes one.
    if (r.state === 'hidden' || r.state === 'unknown') return [{ entityId, kind, state: r.state }];
    if (r.state !== 'estimated' || typeof r.percent !== 'number' || !Number.isFinite(r.percent) || r.percent < 0 || r.percent > 100) return [];
    return [{ entityId, kind, state: 'estimated' as const, percent: Math.round(r.percent) }];
  });
}
export function successEstimateText(estimate: TargetSuccessEstimate): string {
  if (estimate.state === 'hidden') return 'Success estimate hidden';
  const label = estimate.kind === 'contest' ? 'Contest' : estimate.kind === 'hit' ? 'Hit' : 'Armor bending failure';
  // The relay retains casting-success semantics; only presentation shows its complement.
  const percent = estimate.kind === 'cast' ? 100 - (estimate.percent ?? 0) : estimate.percent;
  return `${label}: ${estimate.state === 'estimated' ? `~${percent}%` : 'uncertain'}`;
}
export const SUCCESS_ESTIMATE_TOOLTIP = 'Estimate based on available information; actual results may differ. Casting checks, later effects and interruptions are separate.';
export const CAST_CHECK_TOOLTIP = 'Estimated chance to fail the armor bending roll. Failure spends action points, not mana. A tie triggers wild magic instead of armor failure. Contest is separate.';
export const ARMOR_FAILURE_COST = 'Failure spends AP, not mana.';
